import { MaterialInput } from '#/components/MaterialInput/MaterialInput';
import { ToasterContext } from '#/toaster/toaster.provider';
import React, { useContext, useState } from 'react';
import { View, Text, Pressable } from 'react-native';
import styles from './ProfileCompleteBox.styles';
import handleLink from '#/helpers/handle-link';
import { CheckBox } from 'react-native-web';
import mixins from '#/app/styles';
import Button from '#/components/Button/Button';
import { Me } from '#/api/types';
import { useCompleteProfileMutation } from '#/api/api.portal';

type Props = { profile: Me };

export const ProfileCompleteBox: React.FC<Props> = ({ profile }) => {
  const [isReferralCodeVisible, setReferralCodeVisibility] = useState(false);
  const { mutateAsync: completeProfile, isPending } = useCompleteProfileMutation();
  const { error } = useContext(ToasterContext);
  const [fields, setFields] = useState({
    login: profile.login.split('#')?.at(-1) || '',
    password: '',
    confirmPassword: '',
    email: profile.email.split('#')?.at(-1) || '',
    referralCode: '',
    terms: false,
  });

  const setFieldValue = (field: keyof typeof fields) => (value: string | boolean | number) => {
    setFields(f => ({ ...f, [field]: value }));
  };

  const requiredFields: Array<keyof typeof fields> = [
    'email',
    'password',
    'login',
    'terms',
    'confirmPassword',
  ];

  const isFormValid =
    requiredFields.every(field =>
      typeof fields[field] === 'string' ? fields[field].trim().length > 0 : fields[field],
    ) && fields.password === fields.confirmPassword;

  const handleRegister = async () => {
    const { password, confirmPassword, terms } = fields;

    if (password !== confirmPassword) {
      return error({ message: "Passwords don't match" });
    }
    if (!terms) {
      return error({ message: 'Please accept our terms and conditions' });
    }
    if (!isFormValid) {
      return error({ message: 'Please fill all fields' });
    }

    await completeProfile(fields);
  };

  return (
    <View style={styles.webWrapper}>
      <Text style={styles.webHeader}>Create account</Text>
      <Text style={styles.webSubHeader}>to continue to Streamster</Text>
      <View style={[styles.inputContainer, styles.webInputContainer]}>
        <MaterialInput
          onChangeText={setFieldValue('login')}
          value={fields.login}
          autoComplete="username"
          label="User name"
        />
        <MaterialInput
          onChangeText={setFieldValue('email')}
          value={fields.email}
          autoComplete="email"
          label="Email"
        />
        <MaterialInput
          secureTextEntry
          onChangeText={setFieldValue('password')}
          value={fields.password}
          autoComplete="password"
          label="Password"
        />
        <MaterialInput
          secureTextEntry
          onChangeText={setFieldValue('confirmPassword')}
          value={fields.confirmPassword}
          autoComplete="password"
          label="Confirm password"
        />
        {isReferralCodeVisible ? (
          <MaterialInput
            onChangeText={setFieldValue('referralCode')}
            value={fields.referralCode}
            label="Referral code"
          />
        ) : (
          <Pressable
            onPress={() => {
              setReferralCodeVisibility(true);
            }}
          >
            <Text style={styles.haveReferralCodeText}>Have a referral code?</Text>
          </Pressable>
        )}
        <View style={styles.termsWrapper}>
          <CheckBox
            style={styles.checkbox}
            color={mixins.color.blue}
            value={fields.terms}
            onValueChange={setFieldValue('terms')}
          />
          <Text style={styles.termsText} onPress={() => setFieldValue('terms')(!fields.terms)}>
            I agree to{' '}
            <Text
              style={styles.termsLink}
              onPress={() => handleLink('https://streamster.io/terms-conditions/')}
            >
              Terms & Conditions
            </Text>
          </Text>
        </View>
        <Button onPress={handleRegister} loading={isPending} style={styles.btnRegister}>
          Create
        </Button>
      </View>
    </View>
  );
};
